


"use client";

import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, Video, PlusCircle, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";


import {
  useGetCourseBySlugQuery,
  useEditCourseMutation,
  useDeleteLessonMutation,
  useUploadCourseImageMutation,
  useUploadCourseVideoMutation,
} from "@/features/api/courseApi";

const resolveMediaUrl = (url = "") => {
  if (!url) return "";

  // absolute URL
  if (url.startsWith("http")) return url;

  // already normalized
  if (url.startsWith("/uploads/")) return url;

  // backend bug: file → image
  if (url.startsWith("file/")) {
    url = `image/${url.slice(5)}`;
  }

  return `/uploads/${url}`;
};


export default function EditCoursePage() {
  const navigate = useNavigate();
  const { courseSlug: slug } = useParams();

  const { data, isLoading: courseLoading, error } =
    useGetCourseBySlugQuery(slug);

  const [editCourse, { isLoading: saving }] = useEditCourseMutation();
  const [uploadImage] = useUploadCourseImageMutation();
  const [uploadVideo] = useUploadCourseVideoMutation();
  const [deleteLesson, { isLoading: deletingLesson }] =
  useDeleteLessonMutation();
  
const handleDeleteLesson = async (lessonId) => {
  const confirmed = window.confirm(
    "Are you sure you want to delete this lesson?\nThis action cannot be undone."
  );

  if (!confirmed) return;

  try {
    await deleteLesson({
      courseSlug: course.slug,
      lessonId,
    }).unwrap();

    toast.success("Lesson deleted successfully");
  } catch (err) {
    toast.error(err?.data?.message || "Failed to delete lesson");
  }
};

  const course = data?.course;

  /* -------------------- STATE -------------------- */
  const [input, setInput] = useState({
    title: "",
    slug: "",
    metaDescription: "",
    content: "",
    altText: "",
    featuredImage: "",
    videoUrl: "",
    price: "",
    status: "draft",
    duration: "",
    level: "Beginner",
    language: "English",
    subtitles:  "English",
    certificate: false,
    instructorName: "",
    instructorBio: "",
    learningOutcomes: "",
  });

  const [slugEdited, setSlugEdited] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);

  /* -------------------- LOAD COURSE -------------------- */
  useEffect(() => {
    if (!course) return;

    setInput({
      title: course.title,
      slug: course.slug,
      metaDescription: course.metaDescription || "",
      content: course.content || "",
      altText: course.altText || "",
      featuredImage: course.featuredImage || "",
      videoUrl: course.videoUrl || "",
      price: String(course.price || 0),
      status: course.status || "draft",
      duration: course.duration || "",
      level: course.level || "Beginner",
      language: course.language || "English",
      subtitles: course.subtitles || "",
      certificate: !!course.certificate,
      instructorName: course.instructorName || "",
      instructorBio: course.instructorBio || "",
      learningOutcomes: Array.isArray(course.learningOutcomes)
        ? course.learningOutcomes.join("\n")
        : "",
    });

    if (course.featuredImage)
  setImagePreview(resolveMediaUrl(course.featuredImage));

if (course.videoUrl)
  setVideoPreview(resolveMediaUrl(course.videoUrl));

  }, [course]);

  /* -------------------- HELPERS -------------------- */
  const generateSlug = (title) =>
    title.toLowerCase().trim().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");

  const handleChange = (e) => {
    const { id, value } = e.target;

    setInput((prev) => {
      if (id === "title" && !slugEdited) {
        return { ...prev, title: value, slug: generateSlug(value) };
      }
      if (id === "slug") setSlugEdited(true);
      return { ...prev, [id]: value };
    });
  };

  const handleSelect = (name, value) =>
    setInput((prev) => ({ ...prev, [name]: value }));

  const handleSwitch = (name, value) =>
    setInput((prev) => ({ ...prev, [name]: value }));

  /* -------------------- UPLOAD HELPERS -------------------- */
const uploadMedia = async (file, type) => {
  const fd = new FormData();
  fd.append("file", file);

  try {
    const res =
      type === "image"
        ? await uploadImage(fd).unwrap()
        : await uploadVideo(fd).unwrap();

    return res.fileUrl || res.url || "";
  } catch {
    toast.error("Upload failed");
    return "";
  }
};


  const handleImageUpload = async (e) => {
  const f = e.target.files?.[0];
  e.target.value = "";
  if (!f) return;
  if (!f.type.startsWith("image/")) {
    toast.error("Image only");
    return;
  }

  const url = await uploadMedia(f, "image");
  if (!url) return;

  setInput((prev) => ({ ...prev, featuredImage: url }));
  setImagePreview(resolveMediaUrl(url));
  toast.success("Image uploaded!");
};

const handleVideoUpload = async (e) => {
  const f = e.target.files?.[0];
  e.target.value = ""; 
  if (!f) return;
  if (!f.type.startsWith("video/")) {
    toast.error("Video only");
    return;
  }

  const url = await uploadMedia(f, "video");
  if (!url) return;

  setInput((prev) => ({ ...prev, videoUrl: url }));
  setVideoPreview(resolveMediaUrl(url));
  toast.success("Video uploaded!");
};

  /* -------------------- SUBMIT -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

      const payload = {
        ...input,
        price: Number(input.price) || 0,
        learningOutcomes: input.learningOutcomes
          .split("\n")
          .map((v) => v.trim())
          .filter(Boolean),
      };


    try {
      await editCourse({ slug, data: payload }).unwrap();
      toast.success("Course updated!");
      navigate("/admin/courses");
    } catch (err) {
      toast.error(err?.data?.message || "Update failed");
    }
  };

  /* -------------------- LOADING -------------------- */
  if (courseLoading)
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  if (error)
    return (
      <p className="text-red-500 p-6">
        Failed to load course. Try again later.
      </p>
    );

  /* -------------------- UI (Same as CreateCourse) -------------------- */
  return (
    <div className="space-y-6 p-6">
      <h1 className="text-3xl font-bold">Edit Course</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT SIDE */}
          <div className="lg:col-span-2 space-y-6">

            {/* COURSE INFO */}
            <Card>
              <CardHeader>
                <CardTitle>Course Information</CardTitle>
                <CardDescription>Core details about your course</CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <Label>Course Title</Label>
                <Input id="title" value={input.title} onChange={handleChange} />

                <Label>Description</Label>
                <Textarea id="content" rows={5} value={input.content} onChange={handleChange} />

                <Label>Learning Outcomes</Label>
                <Textarea id="learningOutcomes" rows={3} value={input.learningOutcomes} onChange={handleChange} />
              </CardContent>
            </Card>

            {/* INSTRUCTOR */}
            <Card>
              <CardHeader>
                <CardTitle>Instructor Details</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <Label>Instructor Name</Label>
                <Input id="instructorName" value={input.instructorName} onChange={handleChange} />

                <Label>Instructor Bio</Label>
                <Textarea id="instructorBio" rows={4} value={input.instructorBio} onChange={handleChange} />
              </CardContent>
            </Card>

            {/* MEDIA */}
                <Card>
            <CardHeader>
              <CardTitle>Media Upload</CardTitle>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* ================= IMAGE ================= */}
              <div className="space-y-2">
                <Label>Featured Image</Label>

                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                />

     {imagePreview && (
  <div className="mt-2 flex items-center gap-3">
    <img
      src={imagePreview}
      alt="Featured image preview"
      className="w-32 h-20 rounded border object-cover"
    />

    <Button
      type="button"
      variant="destructive"
      size="icon"
      className="h-8 w-8"
      onClick={() => {
        setImagePreview(null);
        setInput((p) => ({ ...p, featuredImage: "" }));
      }}
      title="Remove image"
    >
      ✕
    </Button>
  </div>
)}

              </div>

              {/* ================= VIDEO ================= */}
              <div className="space-y-2">
                <Label>Intro Video</Label>

                <Input
                  type="file"
                  accept="video/*"
                  onChange={handleVideoUpload}
                />

             {videoPreview && (
  <div className="mt-2 flex items-start gap-3">
  <video controls preload="metadata" className="w-64 rounded border bg-black">
<source src={videoPreview} />
  Your browser does not support the video tag.
</video>


    <Button
      type="button"
      variant="destructive"
      size="icon"
      className="h-8 w-8"
      onClick={() => {
        setVideoPreview(null);
        setInput((p) => ({ ...p, videoUrl: "" }));
      }}
      title="Remove video"
    >
      ✕
    </Button>
  </div>
)}

              </div>
            </CardContent>
          </Card>



            {/* SLUG + META */}
            <Card>
              <CardContent className="space-y-4">
                <Label>Slug</Label>
                <Input id="slug" value={input.slug} onChange={handleChange} />

                <Label>Meta Description</Label>
                <Textarea id="metaDescription" rows={3} value={input.metaDescription} onChange={handleChange} />
              </CardContent>
            </Card>

            {/* LESSON LIST */}
          <Card>
  <CardHeader>
    <CardTitle>Lessons</CardTitle>
  </CardHeader>

  <CardContent className="space-y-4">
    {course?.lessons?.map((lesson) => (
      <div
        key={lesson._id}
        className="flex justify-between items-center border p-3 rounded"
      >
        {/* Lesson title */}
        <span className="font-medium">{lesson.title}</span>

        {/* ACTION ICONS */}
        <div className="flex gap-2">
          {/* EDIT ICON */}
          <Button
            size="sm"
            variant="outline"
            asChild
            title="Edit lesson"
          >
            <Link
              to={`/admin/courses/${course.slug}/lessons/${lesson._id}`}
            >
              <Pencil className="h-4 w-4" />
            </Link>
          </Button>

          {/* DELETE ICON */}
          <Button
            size="sm"
            variant="destructive"
            title="Delete lesson"
            onClick={() => handleDeleteLesson(lesson._id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    ))}

    {/* ADD LESSON */}
    <Button asChild>
      <Link to={`/admin/courses/${course.slug}/add-lesson`}>
        <PlusCircle className="mr-2 h-4 w-4" />
        Add Lesson
      </Link>
    </Button>
  </CardContent>
</Card>

          </div>

          {/* RIGHT SIDE */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Course Settings</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <Label>Status</Label>
                <Select value={input.status} onValueChange={(v) => handleSelect("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>

                <Label>Price (₹)</Label>
                <Input id="price" type="number" value={input.price} onChange={handleChange} />

                <Label>Level</Label>
                <Select value={input.level} onValueChange={(v) => handleSelect("level", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Beginner">Beginner</SelectItem>
                    <SelectItem value="Intermediate">Intermediate</SelectItem>
                    <SelectItem value="Advanced">Advanced</SelectItem>
                  </SelectContent>
                </Select>

                <Label>Language</Label>
                <Input id="language" value={input.language} onChange={handleChange} />

                <Label>Subtitles</Label>
                <Input id="subtitles" value={input.subtitles} onChange={handleChange} />

                <Label>Duration</Label>
                <Input id="duration" value={input.duration} onChange={handleChange} />

                <div className="flex justify-between items-center border p-3 rounded">
                  <div>
                    <Label>Certificate</Label>
                    <p className="text-xs text-muted-foreground">
                      Enable certificate for this course
                    </p>
                  </div>
                  <Switch
                    checked={input.certificate}
                    onCheckedChange={(v) => handleSwitch("certificate", v)}
                  />
                </div>
              </CardContent>
            </Card>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="secondary"
              className="flex-1 h-12"
              onClick={() => navigate("/admin/courses")}
            >
              Back
            </Button>

            <Button
              type="submit"
              className="flex-1 h-12"
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>

          </div>
        </div>
      </form>
    </div>
  );
}

